import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, startWith, take } from 'rxjs/operators';
import { LohiPayService } from '../../../lohi-pay.service';
import { AssetsService } from '../../../root/assets.service';
import { AuthService } from '../../../root/auth.service';
import { DriversService } from '../../../root/drivers.service';
import { InternalService } from '../../../root/internal.service';
import { SnackBarService } from '../../../shared/snackbar.service';
import { ActiveDriver, DriverCertification, DriverDetail, isActiveDriver, LoHiTruck } from '../../../types';

@Component({
  selector: 'lohi-profile',
  templateUrl: './profile.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileComponent implements OnInit {
  @ViewChild('addTruck') private addTruckModal: TemplateRef<any>;
  public currentDriver$: Observable<ActiveDriver>;
  public driverDetail$: Observable<DriverDetail>;
  public driverCutFormControl: FormControl;
  public driverTruckFormControl = new FormControl();
  public driverCDLFormControl = new FormControl();
  public driverEmployeeIDFormControl = new FormControl();
  public newPasswordFormControl = new FormControl(null, [Validators.required, Validators.minLength(6)]);
  public repeatPasswordFormControl = new FormControl(null, [Validators.required, Validators.minLength(6)]);
  public driverAllowShiftBookingControl = new FormControl();
  public removeTrailerFeeControl = new FormControl();
  public driverCertControl = new FormControl();
  public spendingLimitControl = new FormControl();
  public isDriver$: Observable<boolean>;
  public trucks$: Observable<LoHiTruck[]>;
  public addingAsset$$ = new BehaviorSubject<boolean>(false);
  public driverCutHelpAddition$: Observable<string>;
  private addAssetRef: MatDialogRef<TemplateRef<any>>;
  public certs$: Observable<DriverCertification[]>;

  constructor(
    private driversService: DriversService,
    private cd: ChangeDetectorRef,
    private truckService: AssetsService,
    private snackBar: SnackBarService,
    private matDialog: MatDialog,
    public authService: AuthService,
    private lohiPayService: LohiPayService,
    private internalService: InternalService,
  ) {}

  public ngOnInit(): void {
    this.currentDriver$ = this.driversService.currentDriver$.pipe(
      filter((driver) => !!driver),
      map((driver) => (isActiveDriver(driver) ? driver : null)),
      shareReplay(1),
    );
    this.driverDetail$ = this.driversService.driverDetails$;
    this.isDriver$ = this.currentDriver$.pipe(
      map((driver) => {
        if (!driver || !driver.permissions || !driver.permissions.length) {
          return false;
        }
        return driver.permissions.includes('driver');
      }),
    );

    this.trucks$ = this.truckService.trucks$;
    this.certs$ = this.driversService.driverCerts$.pipe(filter((value) => !!value));

    this.setupCerts();
    this.setupDriverCut();
    this.setupDriverTruck();
    this.setupDriverCDL();
    this.setupEmployeeId();
    this.setupAllowShiftBooking();
    this.setupTrailerFee();
    this.setupSpendingLimit();
  }

  public async setupDriverCut() {
    const driver = await this.currentDriver$
      .pipe(
        filter((d) => !!d),
        take(1),
      )
      .toPromise();
    this.driverCutFormControl = new FormControl(driver.driverCut);
    this.cd.markForCheck();

    this.driverCutHelpAddition$ = combineLatest([
      this.authService.userInfo$,
      this.driverCutFormControl.valueChanges.pipe(startWith(driver.driverCut)),
    ]).pipe(
      map(([userInfo, cut]) => {
        return `${userInfo.companyName} Earnings: 100% - ${cut === null ? '?' : cut + '%'} = ${
          cut === null ? '?' : 100 - cut + '%'
        }`;
      }),
    );
  }

  public async setupCerts() {
    const driver = await this.driverDetail$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();
    const certs = await this.certs$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();
    this.driverCertControl = new FormControl((certs || []).filter((value) => (driver.certs || []).includes(value.id)));
  }

  public async updateDriverPercentage() {
    const driver = await this.currentDriver$
      .pipe(
        filter((d) => !!d),
        take(1),
      )
      .toPromise();

    const value = this.driverCutFormControl.value;
    const success = await this.driversService.setDriverPercentage(driver.id, value);
    if (success) {
      this.snackBar.showMessage('Updated Driver Percentage');
    } else {
      this.snackBar.showError('Failed to update percentage');
    }
  }

  public async updateDriverCDL() {
    const driver = await this.currentDriver$
      .pipe(
        filter((d) => !!d),
        take(1),
      )
      .toPromise();

    const value = this.driverCDLFormControl.value;
    const success = await this.driversService.setDriverCDL(driver.id, value);
    if (success) {
      this.snackBar.showMessage('Updated Driver CDL');
    } else {
      this.snackBar.showError('Failed to update CDL');
    }
  }

  public async updateDriverEmployeeId() {
    const driver = await this.currentDriver$
      .pipe(
        filter((d) => !!d),
        take(1),
      )
      .toPromise();

    const value = this.driverEmployeeIDFormControl.value;
    const success = await this.driversService.setDriverEmployeeId(driver.id, value);
    if (success) {
      this.snackBar.showMessage('Updated Employee ID');
    }
  }

  public async addTruckPrompt() {
    if (this.addAssetRef) {
      this.addAssetRef.close();
      this.addAssetRef = null;
    }
    this.addingAsset$$.next(false);
    this.addAssetRef = this.matDialog.open(this.addTruckModal, {
      panelClass: ['w-full', 'md:w-1/2', 'lg:w-1/3'],
    });
  }

  public async resetPassword() {
    const newPassword: string = this.newPasswordFormControl.value;
    const repeatedPassword: string = this.repeatPasswordFormControl.value;
    if (!this.newPasswordFormControl.valid || !newPassword || newPassword !== repeatedPassword) {
      this.snackBar.showError('Password is not valid');
      return;
    }

    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();

    const success = await this.driversService.setDriverPassword(driver.id, newPassword);
    if (success) {
      this.snackBar.showMessage('Password changed');
    }
  }

  private async setupDriverTruck() {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();

    this.driverTruckFormControl.setValue(`${driver.driverTruck}`);
    this.cd.markForCheck();
  }

  private async setupDriverCDL() {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();
    const userInfo = await this.authService.userInfo$.pipe(take(1)).toPromise();

    this.driverCDLFormControl.setValue(driver.cdl);
    if (userInfo.accountRelationship === 'carrier') {
      // OOs
      const isInternal = await this.internalService.isInternal$.pipe(take(1)).toPromise();
      if (!isInternal) {
        this.driverCDLFormControl.disable();
      }
    }
    this.cd.markForCheck();
  }

  private async setupEmployeeId() {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();

    this.driverEmployeeIDFormControl.setValue(driver.employeeId);
    this.cd.markForCheck();
  }

  private async setupAllowShiftBooking() {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();

    this.driverAllowShiftBookingControl.setValue(driver.allowShiftBooking);
    this.cd.markForCheck();
  }

  private async setupTrailerFee() {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();

    this.removeTrailerFeeControl.setValue(driver.removeTrailerFee);
    this.cd.markForCheck();
  }

  private async setupSpendingLimit() {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();
    this.spendingLimitControl.setValue(driver.spendingLimit);
  }

  public async toggleShiftBooking(e: MatSlideToggleChange) {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();

    if (!driver) {
      return;
    }
    const success = await this.driversService.setAllowShiftBookingByUser(
      driver.id,
      e.checked,
      this.removeTrailerFeeControl.value,
    );
    if (success) {
      this.snackBar.showMessage(e.checked ? 'Driver allowed to book shifts' : 'Driver blocked from booking shifts');
    } else {
      this.snackBar.showError('Failed to toggle shift booking');
    }
  }

  public async toggleTrailerFee(e: MatSlideToggleChange) {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();

    if (!driver) {
      return;
    }
    const success = await this.driversService.setAllowShiftBookingByUser(
      driver.id,
      this.driverAllowShiftBookingControl.value,
      e.checked,
    );
    if (success) {
      this.snackBar.showMessage(
        e.checked ? 'Removed Trailer Fee From Estimated Earnings' : 'Added Trailer Fee to Estimated Earnings',
      );
    } else {
      this.snackBar.showError('Failed to toggle trailer fee');
    }
  }

  public async updateSpendingLimit() {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();

    if (!driver) {
      return;
    }
    const success = await this.lohiPayService
      .updateDriverSpendingLimit$(driver.id, this.spendingLimitControl.value)
      .toPromise();
    if (success) {
      this.snackBar.showMessage('Spending Limit Updated');
    } else {
      this.snackBar.showError('Failed to update spending limit');
    }
  }

  public async removeSpendingLimit() {
    const driver = await this.currentDriver$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .toPromise();

    if (!driver) {
      return;
    }
    this.lohiPayService.removeDriverSpendingLimit$(driver.id).subscribe({
      next: () => {
        this.snackBar.showMessage('Spending Limit Removed');
        this.spendingLimitControl.setValue(null);
      },
      error: (err) => {
        this.snackBar.showError('Failed to remove spending limit');
      },
    });
  }

  public async setTruckId() {
    const driver = await this.currentDriver$
      .pipe(
        filter((d) => !!d),
        take(1),
      )
      .toPromise();

    const value = this.driverTruckFormControl.value;
    this.truckService.setDriverTruck$(driver.id, value).subscribe(
      (resp) => {
        this.snackBar.showMessage('Updated Driver Truck');
      },
      (error) => {
        if (error?.error?.messageToUser) {
          this.snackBar.showError(error.error.messageToUser);
        } else {
          this.snackBar.showError('Failed to update truck');
        }
      },
    );
  }

  public async addNewTruck(truckName: string) {
    if (!truckName) {
      return;
    }
    try {
      this.addingAsset$$.next(true);
      const truckId = await this.truckService.addTruck(truckName);
      this.driverTruckFormControl.setValue(truckId);
      this.addAssetRef.close();
    } catch (error) {}
  }

  public certsMapping(certs: { name: string }[]): string[] {
    return certs.map((value) => value.name);
  }

  public async updateCerts() {
    const driver = await this.currentDriver$
      .pipe(
        filter((d) => !!d),
        take(1),
      )
      .toPromise();

    await this.driversService.updateCerts(
      driver.id,
      (this.driverCertControl.value || []).map((value) => value.id),
    );
    this.snackBar.showMessage('Driver Certifications Updated');
  }

  public compareById(a: { id: number }, b: { id: number }): boolean {
    if (a === b) {
      return true;
    }
    return a && b && a.id === b.id;
  }
}
